import React, { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import axiosInstance from "../../../Config/axiosInstance";
import "./AddSSM.css";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ro", ro);

const AddSSM = ({ isOpen, onClose, editingRecord, employerData, onSuccess }) => {
  // State global pentru loading și erori
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Erori individuale pe câmpuri
  const [fieldErrors, setFieldErrors] = useState({});

  // Liste dropdown
  const [ssmTypesOptions, setSsmTypesOptions] = useState([]);
  const [providersOptions, setProvidersOptions] = useState([]);

  // Datele formularului
  const [formData, setFormData] = useState({
    angajat: "",
    ssm_type: "",
    execution_date: "",
    valid_date: "",
    expire_date: "",
    provider: "",
    alert: 5,
  });

  // Refs pentru DatePicker
  const executionRef = useRef(null);
  const validRef = useRef(null);
  const expireRef = useRef(null);

  // Stiluri custom pentru react-select - versiune corectată
  const getCustomSelectStyles = (fieldName) => ({
    control: (base, state) => ({
      ...base,
      border: fieldErrors[fieldName] ? '1px solid #d32f2f' : 
              state.isFocused ? '1px solid #007BFF' : '1px solid #ccc',
      '&:hover': {
        border: fieldErrors[fieldName] ? '1px solid #d32f2f' : 
                state.isFocused ? '1px solid #007BFF' : '1px solid #888'
      },
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      minHeight: '38px',
      backgroundColor: state.isFocused ? '#fff' : '#fff',
      boxShadow: 'none',
      borderRadius: '4px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'left'
    }),
    input: (base) => ({
      ...base,
      color: '#1a1a1a',
      margin: '0',
      padding: '0',
      '& input': {
        boxShadow: 'none !important',
        border: 'none !important',
        outline: 'none !important',
        padding: '0',
        margin: '0',
        textAlign: 'left'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      fontSize: '14px',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1a1a1a',
      fontSize: '14px',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '14px',
      textAlign: 'left'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#e6f2ff' : state.isFocused ? '#f0f0f0' : '#fff',
      color: state.isSelected ? '#006ce4' : '#1a1a1a',
      fontSize: '14px',
      textAlign: 'left',
      '&:active': {
        backgroundColor: '#e6f2ff',
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
      padding: '4px 8px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      marginTop: '8px',
      marginBottom: '8px',
    }),
  });

  // Gestionare modificare select cu react-select
  const handleSelectChange = (field) => (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Reîncărcăm datele când deschidem modalul
  useEffect(() => {
    if (isOpen) {
      // Preluăm tipuri SSM și furnizori
      const fetchData = async () => {
        try {
          const [typesRes, provRes] = await Promise.all([
            axiosInstance.get("/type-ssm/"),
            axiosInstance.get("/suppliers/"),
          ]);

          // Transformă pentru react-select
          const ssmOptions = (typesRes.data || []).map((t) => ({
            value: t.id,
            label: t.type,
          }));
          setSsmTypesOptions(ssmOptions);

          const providerOptions = (provRes.data || []).map((p) => ({
            value: p.id,
            label: p.name,
          }));
          setProvidersOptions(providerOptions);
        } catch (err) {
          console.error("❌ Eroare la încărcarea datelor:", err);
          setError("Nu s-au putut prelua tipurile SSM sau furnizorii.");
        }
      };

      fetchData();

      // Dacă edităm un record, completăm automat datele existente
      if (editingRecord) {
        setFormData({
          angajat: editingRecord.angajat || "",
          ssm_type: editingRecord.ssm_type || "",
          execution_date: editingRecord.execution_date || "",
          valid_date: editingRecord.valid_date || "",
          expire_date: editingRecord.expire_date || "",
          provider: editingRecord.provider || "",
          alert: editingRecord.alert || 5,
        });
      } else {
        // Dacă adăugăm unul nou
        setFormData({
          angajat: employerData?.cod || "",
          ssm_type: "",
          execution_date: "",
          valid_date: "",
          expire_date: "",
          provider: "",
          alert: 5,
        });
      }

      setFieldErrors({});
      setError(null);
    }
  }, [isOpen, editingRecord, employerData]);

  // Gestionăm inputuri simple: number etc.
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Salvăm valorile în formData
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Ștergem eroarea pe câmp
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    // Clasă CSS de stilizare (pentru animații label)
    if (value && value.trim() !== "") {
      e.target.classList.add("filled");
    } else {
      e.target.classList.remove("filled");
    }
  };

  // Gestionăm câmpurile cu date
  const handleDateChange = (field, date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    // Formatăm pentru Django → YYYY-MM-DD
    const formatted = date.toLocaleDateString("en-CA");
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Validare toate câmpurile obligatorii - la fel ca la Medical
  const validateForm = () => {
    const errors = {};

    if (!formData.ssm_type) errors.ssm_type = "SSM Type is required";
    if (!formData.execution_date) errors.execution_date = "Execution date is required";
    if (!formData.valid_date) errors.valid_date = "Valid from date is required";
    if (!formData.expire_date) errors.expire_date = "Valid until date is required";
    if (!formData.provider) errors.provider = "Provider is required";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit formular
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Construim payload trimis către backend
      const submitData = {
        employee: employerData?.id,
        ssm_type: parseInt(formData.ssm_type),
        execution_date: formData.execution_date,
        valid_date: formData.valid_date,
        expire_date: formData.expire_date,
        provider: parseInt(formData.provider),
        alert: parseInt(formData.alert) || 5,
      };

      // EDITARE sau CREARE
      if (editingRecord) {
        await axiosInstance.patch(`ssm/${editingRecord.id}/`, submitData);
      } else {
        await axiosInstance.post("ssm/", submitData);
      }

      // Notificare succes
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 100);

    } catch (err) {
      console.error("❌ Eroare la salvarea datelor SSM:", err);

      // Afișăm erorile primite de la backend pe câmpuri
      if (err.response?.data) {
        const errorMessages = Object.entries(err.response.data)
          .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`
          )
          .join("; ");
        setError(`Eroare: ${errorMessages}`);
      } else {
        setError("Eroare la salvarea datelor.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Nu afișăm modalul dacă nu e deschis
  if (!isOpen) return null;

  return (
    <div className="page-addssm">
      <div className="ssm-modal-overlay">
        <div className="ssm-modal">

          {/* Header modal */}
          <div className="ssm-header">
            <div className="header-left">
              <h2 className="header-title">
                {editingRecord ? "Edit SSM Record" : "Create SSM Record"}
              </h2>
            </div>
            <button onClick={onClose} className="close-btn">×</button>
          </div>

          <hr className="header-divider" />

          {/* Eroare globală */}
          {error && <div className="error-box">{error}</div>}

          {/* FORMULAR */}
          <form onSubmit={handleSubmit}>

            {/* Tip SSM */}
            <div className="form-group">
              <label className="label-left">
                SSM Type <span className="required">*</span>
              </label>

              <Select
                name="ssm_type"
                value={ssmTypesOptions.find(option => option.value === parseInt(formData.ssm_type))}
                onChange={handleSelectChange("ssm_type")}
                options={ssmTypesOptions}
                placeholder="Select SSM Type"
                className={`multiselect-field ${fieldErrors.ssm_type ? 'select-error' : ''}`}
                classNamePrefix="select"
                isSearchable={true}
                isClearable={true}
                styles={getCustomSelectStyles("ssm_type")}
              />

              {fieldErrors.ssm_type && (
                <span className="field-error error-left">{fieldErrors.ssm_type}</span>
              )}
            </div>

            {/* Date: execuție / valabil de la / valabil până */}
            <div className="form-row">
              {[
                { name: "execution_date", label: "Execution Date", ref: executionRef },
                { name: "valid_date", label: "Valid From", ref: validRef },
                { name: "expire_date", label: "Valid Until", ref: expireRef },
              ].map(({ name, label, ref }) => (
                <div key={name} className="form-group">
                  <label className="label-left">
                    {label} <span className="required">*</span>
                  </label>

                  <div className="datepicker-container">
                    <DatePicker
                      ref={ref}
                      selected={formData[name] ? new Date(formData[name]) : null}
                      onChange={(date) => handleDateChange(name, date)}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      className={`datepicker-input-with-icon ${fieldErrors[name] ? "field-error-border" : ""}`}
                      placeholderText="dd.mm.yyyy"
                      popperPlacement="bottom-start"
                    />

                    {/* Icon calendar */}
                    <span
                      className="calendar-icon-inside"
                      onClick={() => ref.current?.setOpen(true)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>

                  {/* Eroare pe câmp */}
                  {fieldErrors[name] && (
                    <span className="field-error error-left">{fieldErrors[name]}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Provider */}
            <div className="form-group">
              <label className="label-left">
                Provider <span className="required">*</span>
              </label>

              <Select
                name="provider"
                value={providersOptions.find(option => option.value === parseInt(formData.provider))}
                onChange={handleSelectChange("provider")}
                options={providersOptions}
                placeholder="Select Provider"
                className={`multiselect-field ${fieldErrors.provider ? 'select-error' : ''}`}
                classNamePrefix="select"
                isSearchable={true}
                isClearable={true}
                styles={getCustomSelectStyles("provider")}
              />

              {fieldErrors.provider && (
                <span className="field-error error-left">{fieldErrors.provider}</span>
              )}
            </div>

            {/* Alert days */}
            <div className="form-group">
              <label className="label-left">Alert (days before expiry)</label>
              <input
                type="number"
                name="alert"
                value={formData.alert}
                onChange={handleInputChange}
                min="1"
                max="365"
                className="input-left"
              />
            </div>

            {/* Butoane */}
            <div className="actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>

              <button type="submit" className="create-btn" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 size={16} className="spinner" /> Saving...
                  </>
                ) : editingRecord ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSSM;